import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './ui.css';

/* ============================================================
   Shared UI primitives — token-driven building blocks used by
   every section of the console.
   ============================================================ */

/* ── PageHeader ── */
export function PageHeader({ title, sub, crumbs, actions }) {
  return (
    <header className="ui-page-header">
      <div>
        {crumbs && crumbs.length > 0 && (
          <nav className="ui-page-header-crumbs" aria-label="Breadcrumb">
            {crumbs.map((c, i) => (
              <React.Fragment key={c.label}>
                {i > 0 && <span className="crumb-sep" aria-hidden="true">/</span>}
                {c.to ? <Link to={c.to}>{c.label}</Link> : <span>{c.label}</span>}
              </React.Fragment>
            ))}
          </nav>
        )}
        <h1 className="ui-page-title">{title}</h1>
        {sub && <p className="ui-page-sub">{sub}</p>}
      </div>
      {actions && <div className="ui-page-actions">{actions}</div>}
    </header>
  );
}

/* ── Card ── */
export function Card({ title, desc, actions, flush, className = '', children, ...rest }) {
  return (
    <section className={`ui-card${flush ? ' ui-card-flush' : ''} ${className}`.trim()} {...rest}>
      {(title || actions) && (
        <div className="ui-card-head">
          <div>
            {title && <h2 className="ui-card-title">{title}</h2>}
            {desc && <p className="ui-card-desc">{desc}</p>}
          </div>
          {actions && <div className="ui-page-actions">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

/* ── StatCard ── */
export function StatGrid({ children }) {
  return <div className="ui-stat-grid">{children}</div>;
}

export function StatCard({ value, label, sub, tone }) {
  return (
    <div className={`ui-stat${tone ? ` ui-stat-${tone}` : ''}`}>
      <div className="ui-stat-value">{value}</div>
      <div className="ui-stat-label">{label}</div>
      {sub && <div className="ui-stat-sub">{sub}</div>}
    </div>
  );
}

/* ── Badge ── */
const STATUS_VARIANTS = {
  succeeded: 'success',
  success: 'success',
  in_sync: 'success',
  matched: 'success',
  completed: 'success',
  running: 'info',
  pending: 'info',
  queued: 'info',
  failed: 'danger',
  error: 'danger',
  missing: 'danger',
  missing_product: 'danger',
  mismatch: 'warning',
  field_mismatch: 'warning',
  missing_field: 'warning',
  ambiguous: 'warning',
  cancelled: 'neutral',
  paused: 'neutral',
};

export function Badge({ variant = 'neutral', children }) {
  return <span className={`ui-badge ui-badge-${variant}`}>{children}</span>;
}

export function StatusBadge({ status, label }) {
  const variant = STATUS_VARIANTS[status] || 'neutral';
  return (
    <span className={`ui-badge ui-badge-${variant}`}>
      {label || String(status || 'unknown').replace(/_/g, ' ')}
    </span>
  );
}

/* ── Button ── */
export function Button({
  variant = 'primary',
  size,
  to,
  type = 'button',
  className = '',
  children,
  ...rest
}) {
  const cls = `ui-btn ui-btn-${variant}${size ? ` ui-btn-${size}` : ''} ${className}`.trim();
  if (to) {
    return (
      <Link to={to} className={cls} {...rest}>
        {children}
      </Link>
    );
  }
  return (
    <button type={type} className={cls} {...rest}>
      {children}
    </button>
  );
}

/* ── Tabs ── */
export function Tabs({ tabs, active, onChange, label = 'Sections' }) {
  return (
    <div className="ui-tabs" role="tablist" aria-label={label}>
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          id={`tab-${t.id}`}
          aria-selected={active === t.id}
          aria-controls={`panel-${t.id}`}
          className="ui-tab"
          onClick={() => onChange(t.id)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function TabPanel({ id, active, children }) {
  if (active !== id) return null;
  return (
    <div role="tabpanel" id={`panel-${id}`} aria-labelledby={`tab-${id}`}>
      {children}
    </div>
  );
}

/* ── SearchInput ── */
export function SearchInput({ value, onChange, placeholder = 'Search…', ...rest }) {
  return (
    <span className="ui-search">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" />
      </svg>
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={rest['aria-label'] || placeholder}
        {...rest}
      />
    </span>
  );
}

/* ── DataTable ── */
export function DataTable({
  columns,
  rows,
  rowKey,
  loading,
  loadingRows = 5,
  empty,
  onRowClick,
  caption,
}) {
  return (
    <div className="ui-table-wrap">
      <table className="ui-table">
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} scope="col" className={col.align ? `align-${col.align}` : undefined} style={col.width ? { width: col.width } : undefined}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading
            ? Array.from({ length: loadingRows }).map((_, i) => (
                <tr key={`skel-${i}`}>
                  {columns.map((col) => (
                    <td key={col.key}>
                      <div className="ui-skel" style={{ height: 14, width: '70%' }} />
                    </td>
                  ))}
                </tr>
              ))
            : rows.map((row, idx) => {
                const key = rowKey ? rowKey(row) : idx;
                return (
                  <tr
                    key={key}
                    className={onRowClick ? 'clickable' : undefined}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {columns.map((col) => (
                      <td key={col.key} className={col.align ? `align-${col.align}` : undefined}>
                        {col.render ? col.render(row) : row[col.key]}
                      </td>
                    ))}
                  </tr>
                );
              })}
          {!loading && rows.length === 0 && (
            <tr>
              <td colSpan={columns.length}>
                {empty || <EmptyState title="Nothing here yet" body="No rows match the current filters." />}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/* ── ProgressBar ── */
export function ProgressBar({ pct, indeterminate, label, tone }) {
  const safePct = Math.max(0, Math.min(100, Math.round(pct || 0)));
  return (
    <div
      className={`ui-progress${indeterminate ? ' ui-progress-indeterminate' : ''}${tone ? ` ui-progress-${tone}` : ''}`}
      role="progressbar"
      aria-valuemin={0}
      {...(indeterminate ? {} : { 'aria-valuemax': 100, 'aria-valuenow': safePct })}
    >
      <div
        className="ui-progress-fill"
        style={indeterminate ? undefined : { width: `${safePct}%` }}
      />
      {label && <span className="ui-progress-text">{label}</span>}
    </div>
  );
}

/* ── EmptyState ── */
export function EmptyState({ title, body, action }) {
  return (
    <div className="ui-empty">
      <h3>{title}</h3>
      {body && <p>{body}</p>}
      {action}
    </div>
  );
}

/* ── Skeleton ── */
export function Skeleton({ height = 80, style }) {
  return <div className="ui-skel" style={{ height, ...style }} aria-hidden="true" />;
}

/* ── Toast ── */
export function Toast({ message, error }) {
  if (!message) return null;
  return (
    <div className={`ui-toast${error ? ' ui-toast-error' : ''}`} role="status" aria-live="polite">
      {message}
    </div>
  );
}

/* ── ConfirmModal ── */
export function ConfirmModal({
  title,
  body,
  confirmLabel = 'Confirm',
  danger,
  onConfirm,
  onCancel,
  busy,
  requireText,
}) {
  const [typed, setTyped] = useState('');
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const textGateFailed = Boolean(requireText) && typed.trim() !== requireText;

  return (
    <div className="ui-modal-backdrop" onClick={onCancel} role="presentation">
      <div
        className="ui-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <h3>{title}</h3>
        <p>{body}</p>
        {requireText && (
          <div className="form-group">
            <label className="form-label" htmlFor="ui-modal-require-text">
              Type <strong>{requireText}</strong> to confirm
            </label>
            <input
              id="ui-modal-require-text"
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={requireText}
              autoFocus
              autoComplete="off"
            />
          </div>
        )}
        <div className="ui-modal-actions">
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            onClick={onConfirm}
            disabled={busy || textGateFailed}
          >
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
